import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PhoneOff, Headphones, MessageCircle, Users } from 'lucide-react';
import { create } from 'zustand';
import { api, ApiError, errorText } from '@/lib/api';
import { PageHeader, Stat, DataTable, ErrorBox } from '@/components/common';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusBadge, Badge } from '@/components/ui/badge';
import { AgentStateButtons, CampaignStatusButtons } from './Config';
import { fmtDuration, pct, enumLabel } from '@/lib/utils';
import { t } from '@/i18n';
import { toast } from '@/store/toast';
import { useAuth, useCan } from '@/store/auth';
import { usePhone } from '@/store/phone';
import { useLiveRefresh, usePoll } from '@/hooks/live';
import { useRealtime, type LiveEvent } from '@/store/realtime';

type BargeMode = 'listen' | 'whisper' | 'full';
// English labels: translated where they are shown
const BARGE_MODES: { mode: BargeMode; label: string; aria: string; stop: string; icon: typeof Headphones }[] = [
  { mode: 'listen', label: 'Listen', aria: 'Listen to {{code}}', stop: 'Stop listen', icon: Headphones },
  { mode: 'whisper', label: 'Whisper', aria: 'Whisper to {{code}}', stop: 'Stop whisper', icon: MessageCircle },
  { mode: 'full', label: 'Barge in', aria: 'Barge in to {{code}}', stop: 'Stop full', icon: Users },
];

// The supervisor's barge in progress lives outside the Dashboard component, so leaving the page and
// coming back keeps its Stop button (before, the buttons came back and a second barge rang the busy
// softphone, which answered 486 Busy Here). On each visit it is synced with the engine.
type Barge = { agentCode: string; mode: BargeMode; uuid: string };
const useBarge = create<{ barge: Barge | null; set: (b: Barge | null) => void }>((set) => ({ barge: null, set: (barge) => set({ barge }) }));

function BargeButtons({ agent, active, onStart, onStop }: {
  agent: any; active: { agentCode: string; mode: BargeMode; uuid: string } | null;
  onStart: (agentCode: string, mode: BargeMode) => void; onStop: () => void;
}) {
  const user = useAuth((s) => s.user);
  const registered = usePhone((s) => s.reg === 'registered');
  const allowed = useCan('dashboard.barge');
  if (!allowed || !user?.agent_id || agent.state !== 'IN_CALL' || Number(agent.id) === Number(user.agent_id)) return null;
  if (active && active.agentCode === agent.agent_code) {
    const stop = BARGE_MODES.find((m) => m.mode === active.mode)?.stop;
    return <Button size="sm" variant="destructive" onClick={onStop}>{stop ? t(stop) : `Stop ${active.mode}`}</Button>;
  }
  if (active) return null;   // one barge session at a time
  return (
    <div className="flex gap-1">
      {BARGE_MODES.map(({ mode, label, aria, icon: Icon }) => (
        <Button key={mode} size="icon" variant="ghost" aria-label={t(aria, { code: agent.agent_code })} title={t(label)}
          disabled={!registered} onClick={() => onStart(agent.agent_code, mode)}><Icon /></Button>
      ))}
    </div>
  );
}

export default function Dashboard() {
  const qc = useQueryClient();
  const p3 = usePoll(3000), p5 = usePoll(5000), p15 = usePoll(15000);
  const live = useQuery({ queryKey: ['live'], queryFn: () => api('/reports/live'), refetchInterval: p5 });
  const today = useQuery({ queryKey: ['summary-today'], queryFn: () => api('/reports/summary', { query: { from: 'today' } }), refetchInterval: 30000 });
  // the agents and campaigns cards need those sections' view right (PHASE23)
  const [seeAgents, seeCampaigns, canHangup] = [useCan('agents.view'), useCan('campaigns.view'), useCan('dashboard.hangup')];
  const agents = useQuery({ queryKey: ['dash-agents'], queryFn: () => api<{ rows: any[] }>('/agents', { query: { limit: 500 } }), refetchInterval: p5, enabled: seeAgents });
  const calls = useQuery({ queryKey: ['dash-calls'], queryFn: () => api<{ rows: any[] }>('/calls/live'), refetchInterval: p3 });
  const campaigns = useQuery({ queryKey: ['dash-campaigns'], queryFn: () => api<{ rows: any[] }>('/campaigns', { query: { limit: 100 } }), refetchInterval: p15, enabled: seeCampaigns });
  // Phase 15: refresh at once on real-time events instead of waiting for the next poll
  useLiveRefresh(['agent.state', 'call.created', 'call.ringing', 'call.answered', 'call.bridged', 'call.hangup', 'queue.updated'],
    () => { for (const k of ['live', 'dash-agents', 'dash-calls']) qc.invalidateQueries({ queryKey: [k] }); });
  useLiveRefresh(['campaign.updated'], () => qc.invalidateQueries({ queryKey: ['dash-campaigns'] }));

  const l = live.data ?? {};
  const liveQueues: { queue_id: number; name: string; waiting: number; longest_wait_sec: number }[] = Array.isArray(l.queues) ? l.queues : [];
  const o = today.data?.outbound ?? {};
  const i = today.data?.inbound ?? {};
  const states: Record<string, number> = l.agents && typeof l.agents === 'object' ? l.agents : {};

  async function hangup(uuid: string) {
    if (!confirm(t('Hang up this call?'))) return;
    try { await api(`/calls/${uuid}/hangup`, { method: 'POST' }); toast.ok(t('Hung up')); calls.refetch(); } catch (e) { toast.error(errorText(e)); }
  }

  const barging = useBarge((s) => s.barge);
  const setBarging = useBarge((s) => s.set);
  const hasExtension = !!useAuth((s) => s.user?.agent_id);
  React.useEffect(() => {
    if (!hasExtension) return;
    api<{ barge?: { uuid: string; mode: BargeMode; target: string } | null }>('/admin/barge')
      .then((r) => setBarging(r.barge?.uuid ? { agentCode: r.barge.target, mode: r.barge.mode, uuid: r.barge.uuid } : null))
      .catch(() => {});
  }, [hasExtension, setBarging]);
  // the barge leg rings this user's own softphone: answer it automatically, and forget the session
  // once that call is over (hung up from the softphone, or the agent's call ended)
  const phoneCall = usePhone((s) => s.call);
  const bargeAnswered = React.useRef(false);
  React.useEffect(() => {
    if (!barging) { bargeAnswered.current = false; return; }
    if (phoneCall === 'active') bargeAnswered.current = true;
    else if (bargeAnswered.current) { bargeAnswered.current = false; setBarging(null); }
  }, [phoneCall, barging]);
  async function startBarge(agentCode: string, mode: BargeMode) {
    if (usePhone.getState().call !== 'idle') { toast.error(t('Finish the call on your softphone first')); return; }
    try {
      usePhone.getState().expectCall(15000);
      const r = await api<{ ok: boolean; uuid: string }>('/admin/barge/start', { body: { agent_code: agentCode, mode } });
      setBarging({ agentCode, mode, uuid: r.uuid });
      toast.ok(t(mode === 'full' ? 'Barged into {{code}}' : mode === 'whisper' ? 'Whispering to {{code}}' : 'Listening to {{code}}', { code: agentCode }));
    } catch (e) {
      if (e instanceof ApiError && e.code === 'BARGE_IN_PROGRESS' && typeof e.body.uuid === 'string') {
        setBarging({ agentCode: String(e.body.target ?? ''), mode: (e.body.mode as BargeMode) ?? 'listen', uuid: e.body.uuid });
        toast.error(t('You are already barging: stop that one first'));
      } else toast.error(errorText(e));
    }
  }
  async function stopBarge() {
    if (!barging) return;
    try { await api('/admin/barge/stop', { body: { uuid: barging.uuid } }); } catch (e) { toast.error(errorText(e)); }
    setBarging(null);
  }

  return (
    <div className="grid gap-4">
      <PageHeader title={t('Dashboard')} description={t('Live view of calls, agents and campaigns.')} />
      <ErrorBox error={live.error} />
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4 xl:grid-cols-7">
        <Stat label={t('Live calls')} value={l.concurrent ?? '-'} />
        <Stat label={t('Waiting in queue')} value={l.queue_waiting ?? '-'} />
        <Stat label={t('Longest wait')} value={liveQueues.length ? fmtDuration(liveQueues[0].longest_wait_sec) : '-'}
          hint={liveQueues.length ? liveQueues.slice(0, 3).map((q) => `${q.name} ${fmtDuration(q.longest_wait_sec)} (${q.waiting})`).join(' · ') : t('nobody waiting')} />
        <Stat label={t('Calls last minute')} value={l.calls_last_min ?? '-'} />
        <Stat label={t('Agents ready')} value={states.READY ?? 0} hint={t('in call {{inCall}} · paused {{paused}} · wrap-up {{wrapup}}', { inCall: states.IN_CALL ?? 0, paused: states.PAUSED ?? 0, wrapup: states.WRAPUP ?? 0 })} />
        <Stat label={t('Calls today')} value={today.data?.calls ?? '-'} hint={t('peak {{n}} concurrent', { n: today.data?.concurrent_peak ?? 0 })} />
        <Stat label={t('ASR today')} value={pct(o.asr)} hint={t('ACD {{acd}} · abandon {{abandon}}', { acd: fmtDuration(o.acd_sec), abandon: pct(o.abandon_rate) })} />
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        {seeAgents && <Card>
          <CardHeader><CardTitle>{t('Agents')}</CardTitle></CardHeader>
          <CardContent>
            <DataTable rows={(agents.data?.rows ?? []).filter((a) => a.state !== 'LOGGED_OUT')} empty={t('No agents logged in.')}
              columns={[{ key: 'agent_code', label: t('Agent') }, { key: 'name', label: t('Name') },
                { key: 'state', label: t('State'), render: (r) => <StatusBadge value={r.state} /> },
                { key: 'state_reason', label: t('Reason') },
                { key: 'sip_registered', label: 'SIP', render: (r) => Number(r.sip_registered) ? <Badge variant="success">{t('on')}</Badge> : <Badge variant="destructive">{t('off')}</Badge> },
                { key: 'calls_handled', label: t('Calls') }]}
              actions={(r) => <div className="flex items-center gap-1">
                <BargeButtons agent={r} active={barging} onStart={startBarge} onStop={stopBarge} />
                <AgentStateButtons agent={r} onDone={() => agents.refetch()} />
              </div>} />
          </CardContent>
        </Card>}
        <Card>
          <CardHeader><CardTitle>{t('Live calls')}</CardTitle></CardHeader>
          <CardContent>
            <DataTable rowKey={(r) => r.call_uuid} rows={calls.data?.rows} empty={t('No calls up right now.')}
              columns={[{ key: 'direction', label: t('Dir'), render: (r) => enumLabel(r.direction) }, { key: 'status', label: t('Status'), render: (r) => <StatusBadge value={r.status} /> },
                { key: 'source', label: t('From') }, { key: 'destination', label: t('To') }, { key: 'agent_code', label: t('Agent') },
                { key: 'age_sec', label: t('Age'), render: (r) => fmtDuration(r.age_sec) }]}
              actions={canHangup ? (r) => <Button size="icon" variant="ghost" aria-label={t('Hang up')} onClick={() => hangup(r.call_uuid)}><PhoneOff className="text-destructive" /></Button> : undefined} />
          </CardContent>
        </Card>
      </div>
      <div className="grid gap-4 xl:grid-cols-2">
        {seeCampaigns && <Card>
          <CardHeader><CardTitle>{t('Campaigns')}</CardTitle></CardHeader>
          <CardContent>
            <DataTable rows={campaigns.data?.rows} columns={[{ key: 'name', label: t('Name') }, { key: 'dial_method', label: t('Method'), render: (r) => enumLabel(r.dial_method) },
              { key: 'status', label: t('Status'), render: (r) => <StatusBadge value={r.status} /> }]}
              actions={(r) => <CampaignStatusButtons campaign={r} onDone={() => campaigns.refetch()} />} />
          </CardContent>
        </Card>}
        <Card>
          <CardHeader><CardTitle>{t('Today')}</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
            <div className="font-medium">{t('Outbound')}</div><div className="font-medium">{t('Inbound')}</div>
            <div>{t('Attempts {{attempts}}, answered {{answered}}', { attempts: o.attempts ?? 0, answered: o.answered ?? 0 })}</div><div>{t('Calls {{calls}}, handled {{handled}}', { calls: i.calls ?? 0, handled: i.handled ?? 0 })}</div>
            <div>{t('Busy {{busy}}, no answer {{noAnswer}}', { busy: pct(o.busy_rate), noAnswer: pct(o.no_answer_rate) })}</div><div>{t('Abandon {{n}}', { n: pct(i.abandon_rate) })}</div>
            <div>{t('Abandoned {{n}}', { n: o.abandoned ?? 0 })}</div><div>{t('Average wait {{d}}', { d: fmtDuration(i.avg_wait_sec) })}</div>
            <div>{t('Talk time {{d}}', { d: fmtDuration(today.data?.talk_sec) })}</div><div>{t('Peak CPS {{n}}', { n: today.data?.cps_peak ?? 0 })}</div>
          </CardContent>
        </Card>
      </div>
      <EventFeed />
    </div>
  );
}

// what an event says, in a few words
const CALL_STEP: Record<string, string> = { 'call.ringing': 'call {{id}} ringing', 'call.answered': 'call {{id}} answered', 'call.bridged': 'call {{id}} bridged' };
export function describeEvent(e: LiveEvent): string {
  const d = e.data;
  switch (e.event) {
    case 'agent.state': return d.reason === 'SIP_REGISTRATION'
      ? t(d.registered ? 'agent {{code}} phone registered' : 'agent {{code}} phone unregistered', { code: d.code ?? d.agent_id })
      : `${t('agent {{code}}: {{from}} → {{to}}', { code: d.code ?? d.agent_id, from: d.from ?? '?', to: d.to })}${d.reason ? ` (${d.reason})` : ''}`;
    case 'disposition.required': return t('agent {{code}} must disposition the call', { code: d.code ?? d.agent_id });
    case 'call.created': {
      const dir = String(d.direction ?? '').toLowerCase();
      return t('{{direction}} call {{destination}}', { direction: dir ? t(dir) : '', destination: d.destination ?? '' }).trim();
    }
    case 'call.ringing': case 'call.answered': case 'call.bridged':
      return `${t(CALL_STEP[e.event], { id: String(d.call_uuid ?? '').slice(0, 8) })}${d.agent_id ? ` · ${t('agent #{{id}}', { id: d.agent_id })}` : ''}`;
    case 'call.hangup': return `${t('call {{id}} ended', { id: String(d.call_uuid ?? '').slice(0, 8) })}${d.hangup_cause ? ` (${d.hangup_cause})` : ''}`;
    case 'queue.updated': return d.queue_id ? t('queue #{{queue}}: call {{id}} {{status}}', { queue: d.queue_id, id: String(d.call_uuid ?? '').slice(0, 8), status: d.status ?? '' }) : t('queue changed');
    case 'campaign.updated': return d.broadcast_id ? t('broadcast #{{id}} {{status}}', { id: d.broadcast_id, status: d.status })
      : t('campaign #{{id}} {{status}}', { id: d.campaign_id, status: d.status ?? d.action ?? t('changed') });
    case 'contact.updated': return t('contact #{{id}} {{from}} → {{to}}', { id: d.contact_id, from: d.from, to: d.to });
    case 'dialer.updated': return `${t('dialer campaign #{{id}}: ready {{ready}}, dialed {{dialed}}', { id: d.campaign_id, ready: d.ready, dialed: d.dialed })}${d.reason ? ` (${d.reason})` : ''}`;
    default: return e.event;
  }
}

function EventFeed() {
  const recent = useRealtime((s) => s.recent);
  const status = useRealtime((s) => s.status);
  return (
    <Card>
      <CardHeader><CardTitle>{t('Live events')}</CardTitle></CardHeader>
      <CardContent>
        {recent.length === 0
          ? <p className="text-sm text-muted-foreground">{status === 'live' ? t('Waiting for activity…') : t('Live events are not connected; the page refreshes by polling.')}</p>
          : (
            <ul className="grid max-h-80 gap-1 overflow-y-auto text-sm" data-testid="event-feed">
              {recent.slice(0, 30).map((e, i) => (
                <li key={`${e.at}-${i}`} className="flex gap-3" data-event={e.event}>
                  <span className="w-20 shrink-0 tabular-nums text-muted-foreground">{new Date(e.at).toLocaleTimeString()}</span>
                  <Badge variant="outline" className="w-36 shrink-0 justify-center">{e.event}</Badge>
                  <span className="min-w-0 truncate">{describeEvent(e)}</span>
                </li>
              ))}
            </ul>
          )}
      </CardContent>
    </Card>
  );
}
